import type { NavigationGuardNext, RouteLocationNormalized, Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { authProvider, currentAuthProviderKind } from '@/services/currentAuthProvider'
import { refreshIdToken } from '@/services/sessionExpiry'
import { onAdminSessionExpired, rememberAdminSessionExpired } from '@/services/adminSessionExpiry'
import { isAdminAccessDenied, rememberAdminAccessDenied } from '@/services/adminAccessDenied'
import { useAdminAuth } from '@/composables/admin/useAdminAuth'
import { initApiService } from '@/services/init'
import { config } from '@/config'
import { getAxiosStatus } from '@/utils/errorHandling'

/**
 * Admin portal authentication guard.
 *
 * For every authenticated route we verify three things, in order:
 * 1. The session is not past `expiresAt` (signed out + redirect to login).
 * 2. The admin has at least one Firebase MFA factor enrolled (otherwise:
 *    redirect to /admin/mfa-setup). A Keycloak session never goes there: the
 *    realm enrols and asks for TOTP itself, so that screen has nothing to offer it.
 * 3. The current ID token carries the `firebase.sign_in_second_factor`
 *    claim — proving this session passed the MFA challenge. If not, force
 *    a fresh sign-in so the next session goes through MFA.
 *
 * Route meta:
 * - `requiresAuth: false` → public route (login, mfa-setup falls through here)
 * - `requiresMfa: false`  → authenticated but allowed without an MFA-backed
 *                           session (used by /admin/mfa-setup so the admin
 *                           can enrol before having a second factor).
 */
export async function adminAuthGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false)
  const requiresMfa = to.matched.some((record) => record.meta.requiresMfa !== false)

  if (!requiresAuth) {
    if (to.name === 'admin-login' && authStore.isAuthenticated && !isAdminAccessDenied()) {
      try {
        const state = await checkAdminSession()
        if (state.sessionExpired) {
          await handleSessionExpired()
          if (onKeycloak() && to.query.expired !== 'true') {
            return next({ name: 'admin-login', query: { ...to.query, expired: 'true' } })
          }
          return next()
        }
        if (!state.mfaEnrolled) {
          return next({ name: 'admin-mfa-setup' })
        }
        if (!state.sessionHasSecondFactor) {
          // Admin has MFA enrolled but the current token is from a session
          // that did not complete MFA — force a fresh sign-in.
          await handleSessionExpired()
          return next()
        }
        return next({ name: 'admin-dashboard' })
      } catch {
        console.warn('[Admin Auth] Token refresh failed, allowing login page access')
        return next()
      }
    }
    return next()
  }

  if (!authStore.isAuthenticated) {
    return next({ name: 'admin-login', query: { redirect: to.fullPath } })
  }

  let state: AdminSessionState
  try {
    state = await checkAdminSession()
  } catch {
    console.warn('[Admin Auth] Token refresh failed, redirecting to login')
    return next({ name: 'admin-login', query: { redirect: to.fullPath } })
  }

  if (state.sessionExpired) {
    await handleSessionExpired()
    return next({
      name: 'admin-login',
      query: { redirect: to.fullPath, expired: 'true' }
    })
  }

  if (to.name === 'admin-mfa-setup' && onKeycloak()) {
    return next({ name: 'admin-dashboard' })
  }

  if (requiresMfa) {
    if (!state.mfaEnrolled) {
      return next({ name: 'admin-mfa-setup', query: { redirect: to.fullPath } })
    }
    if (!state.sessionHasSecondFactor) {
      // Enrolled but this session has no second factor — re-authenticate.
      await handleSessionExpired()
      return next({
        name: 'admin-login',
        query: { redirect: to.fullPath, expired: 'true' }
      })
    }
  }

  const { adminUser, fetchAdminUser } = useAdminAuth()
  if (!adminUser.value) {
    try {
      await fetchAdminUser()
    } catch (e) {
      if (getAxiosStatus(e) === 403) {
        await endRefusedSession()
        return next({ name: 'admin-login' })
      }
      return next({ name: 'admin-login', query: { redirect: to.fullPath } })
    }
  }

  next()
}

/** `AdminClaimsSyncService.SESSION_TIMEOUT` and the realm's `ssoSessionMaxLifespan`; move them together. */
const KEYCLOAK_ADMIN_SESSION_SECONDS = 60 * 60

interface AdminSessionState {
  mfaEnrolled: boolean
  sessionHasSecondFactor: boolean
  sessionExpired: boolean
}

/**
 * Inspect the current session for admin-relevant state.
 *
 * Forces a token refresh so we observe the latest custom claims (essential
 * on page refresh where the persisted token may be stale).
 *
 * @throws when the token refresh itself fails (treated as invalid session).
 */
async function checkAdminSession(): Promise<AdminSessionState> {
  if (onKeycloak()) return checkKeycloakSession()

  if (!authProvider.currentSession()) {
    return { mfaEnrolled: false, sessionHasSecondFactor: false, sessionExpired: false }
  }

  // E2E carve-out: the Firebase Auth Emulator does not implement TOTP MFA
  // storage (#2359), so the suite cannot enrol a second factor. When the
  // E2E build sets `e2eBypassMfa`, treat any authenticated admin as if they
  // had completed MFA. Production never sets this flag.
  if (config.e2eBypassMfa) {
    return { mfaEnrolled: true, sessionHasSecondFactor: true, sessionExpired: false }
  }

  const claims = (await authProvider.getTokenClaims(true)) ?? {}
  const mfaEnrolled = (await authProvider.mfa.getEnrolledFactors()).length > 0

  return {
    mfaEnrolled,
    sessionHasSecondFactor: tokenHasSecondFactor(claims),
    sessionExpired: isPast(sessionExpiresAt(claims))
  }
}

/**
 * The admin realm admits no sign-in without its TOTP code (ADR-045), so the only question is
 * whether the session is alive. `currentSession()` cannot answer it: it reads `null` for an access
 * token that has merely lapsed, as after a laptop sleep, so only a renewal can tell the two apart.
 * A failure that says nothing about the session, such as Keycloak being unreachable, is thrown.
 */
async function checkKeycloakSession(): Promise<AdminSessionState> {
  const realmEnforcesMfa = { mfaEnrolled: true, sessionHasSecondFactor: true }
  let claims: Record<string, unknown> | null
  try {
    claims = await authProvider.getTokenClaims(true)
  } catch (error) {
    if (!authProvider.isSessionExpiredError(error)) throw error
    return { ...realmEnforcesMfa, sessionExpired: true }
  }
  return { ...realmEnforcesMfa, sessionExpired: !claims || isPast(sessionExpiresAt(claims)) }
}

function onKeycloak(): boolean {
  return currentAuthProviderKind() === 'keycloak-admin'
}

function isPast(epochSeconds: number | undefined): boolean {
  return epochSeconds !== undefined && Math.floor(Date.now() / 1000) > epochSeconds
}

/**
 * When this admin session ends, in epoch seconds — or `undefined` when the
 * token carries no session to end; for Keycloak, an hour after `auth_time`.
 *
 * `expiresAt` is a user-level Firebase custom claim set by startAdminSession
 * after the MFA challenge — it persists across sign-outs. Treat it as
 * meaningful only when the current token actually carries the second factor;
 * otherwise it's a stale claim from a previous session and would falsely
 * expire a fresh sign-in (e.g. on the /admin/mfa-setup bootstrap path).
 */
function sessionExpiresAt(claims: Record<string, unknown>): number | undefined {
  if (onKeycloak()) {
    const authTime = claims.auth_time
    return typeof authTime === 'number' ? authTime + KEYCLOAK_ADMIN_SESSION_SECONDS : undefined
  }
  if (!tokenHasSecondFactor(claims)) return undefined
  const expiresAt = claims.expiresAt
  return typeof expiresAt === 'number' ? expiresAt : undefined
}

function tokenHasSecondFactor(claims: Record<string, unknown>): boolean {
  const firebaseClaim = claims['firebase'] as { sign_in_second_factor?: unknown } | undefined
  const secondFactor = firebaseClaim?.sign_in_second_factor
  return typeof secondFactor === 'string' && secondFactor.length > 0
}

/**
 * On Keycloak the sign-out leaves the page and does not resolve, so nothing the caller does after
 * it runs: the reason is recorded first, and a sign-out that fails still reaches the login page.
 */
async function handleSessionExpired(): Promise<void> {
  console.warn('[Admin Auth] Session expired or missing MFA, signing out')
  const { signOut } = useAdminAuth()
  if (!onKeycloak()) return signOut()

  rememberAdminSessionExpired()
  try {
    await signOut()
  } catch (e) {
    console.warn('[Admin Auth] Sign-out of an ended session failed', e)
  }
}

/**
 * The backend does not admit this person, and no sign-in state will change that, so the session
 * ends here. Without this the login route sends a session-holder straight back and the two
 * redirect at each other. The refusal is recorded first: Keycloak's sign-out leaves the page, and
 * if it fails the login route still has to hold.
 */
async function endRefusedSession(): Promise<void> {
  rememberAdminAccessDenied()
  try {
    await useAdminAuth().signOut()
  } catch (e) {
    console.warn('[Admin Auth] Sign-out of a refused session failed', e)
  }
}

/** Guards against the timer and several refused requests each ending the session. */
let expiring = false

/**
 * End an expired session from an open page: sign out and show the login page's
 * expired-session notice, keeping where the admin was so they return there.
 */
async function expireOpenSession(router: Router): Promise<void> {
  if (expiring) return
  expiring = true
  try {
    const current = router.currentRoute.value
    await handleSessionExpired()
    if (current.name !== 'admin-login') {
      await router.push({
        name: 'admin-login',
        query: { redirect: current.fullPath, expired: 'true' }
      })
    }
  } finally {
    expiring = false
  }
}

/**
 * Watch the admin session while a page stays open. The router guard only
 * answers on a navigation, so a page left alone would never notice its hour
 * ending (#4677). Two things end it here, both through the guard's own sign-out:
 *
 * - a timer set from the `expiresAt` claim — no polling of the backend;
 * - the backend's `admin-session-expired` 401, which is what a tab whose timer
 *   was suspended, or whose clock disagrees with the server's, runs into.
 *
 * @returns a function that stops watching.
 */
export function watchAdminSession(router: Router): () => void {
  const expire = () => void expireOpenSession(router)
  const stopListening = onAdminSessionExpired(expire)
  let timer: ReturnType<typeof setTimeout> | undefined
  let stopped = false

  // Same carve-out as checkAdminSession: an E2E session has no second factor
  // and so no expiry to time.
  if (authProvider.currentSession() && !config.e2eBypassMfa) {
    // Not force-refreshed: the guard did that before this page rendered.
    authProvider.getTokenClaims().then((claims) => {
      const expiresAt = claims ? sessionExpiresAt(claims) : undefined
      if (stopped || expiresAt === undefined) return
      timer = setTimeout(expire, Math.max(0, expiresAt * 1000 - Date.now()))
    }).catch(() => {
      // No readable token means no claim to time; the next navigation or
      // refused request ends the session instead.
    })
  }

  return () => {
    stopped = true
    clearTimeout(timer)
    stopListening()
  }
}

/**
 * Start a new admin session by calling the backend session endpoint.
 * Sets the `expiresAt` claim for the 1-hour session timeout. Must be called
 * after successful authentication AND MFA challenge resolution so the token
 * already carries `firebase.sign_in_second_factor`.
 */
export async function startAdminSession(): Promise<void> {
  const apiService = initApiService()
  await apiService.adminAuth.startSession()

  await refreshIdToken()
}
